import { AlertTriangle, CheckCircle2, Circle, Clock, Loader2, XCircle } from "lucide-react";
import type { CfoFundingEvent, CfoFundingProgress, CfoFundingStage } from "@/api/types";
import { CFO_DECISIONS } from "@/lib/cfoLabels";
import { formatDateTime, formatRelativeDate } from "@/lib/formatters";
import { cn } from "@/lib/utils";
import { costLabel, countOf, etaLabel } from "./fundingProgress";

const STATE: Record<
  CfoFundingStage["state"],
  { icon: typeof Circle; tone: string; bar: string; label: string }
> = {
  pending: { icon: Circle, tone: "text-slate-300", bar: "bg-slate-200", label: "Not started" },
  running: { icon: Loader2, tone: "text-indigo-600", bar: "bg-indigo-600", label: "Running" },
  waiting: { icon: Clock, tone: "text-amber-500", bar: "bg-amber-400", label: "Waiting" },
  done: { icon: CheckCircle2, tone: "text-emerald-600", bar: "bg-emerald-500", label: "Done" },
  failed: { icon: XCircle, tone: "text-rose-600", bar: "bg-rose-500", label: "Failed" },
};

function decisionLabel(value: string): string {
  return CFO_DECISIONS[value]?.label ?? value.replace(/_/g, " ").toLowerCase();
}

function Stage({ stage }: { stage: CfoFundingStage }) {
  const look = STATE[stage.state];
  const Icon = look.icon;
  const total = stage.total ?? 0;
  const settled = (stage.done ?? 0) + (stage.failed ?? 0);
  const extra = [
    stage.queued ? `${stage.queued} to run` : null,
    stage.waiting ? `${stage.waiting} waiting` : null,
    stage.failed ? `${stage.failed} failed` : null,
  ].filter(Boolean);
  return (
    <li className="space-y-1.5">
      <div className="flex items-center gap-2.5 text-sm">
        <Icon
          className={cn("h-4 w-4 shrink-0", look.tone, stage.state === "running" && "animate-spin")}
          aria-hidden
        />
        <span className="font-medium text-slate-800">{stage.label}</span>
        <span className="sr-only">{look.label}</span>
        <span className="ml-auto tabular-nums text-xs text-slate-500">
          {total ? countOf(stage) : look.label}
          {extra.length > 0 && <span className="text-slate-400"> · {extra.join(" · ")}</span>}
        </span>
      </div>
      {total > 0 && (
        <div className="ml-6 h-1.5 overflow-hidden rounded-full bg-slate-100">
          <div
            className={cn("h-full rounded-full transition-all duration-700", look.bar)}
            style={{ width: `${Math.round((settled / total) * 100)}%` }}
          />
        </div>
      )}
      {stage.detail && (
        <p
          className={cn(
            "ml-6 text-xs",
            stage.state === "failed" ? "text-rose-700" : stage.state === "waiting" ? "text-amber-700" : "text-slate-500",
          )}
        >
          {stage.detail}
        </p>
      )}
    </li>
  );
}

function Event({ event }: { event: CfoFundingEvent }) {
  const tone =
    event.outcome === "failed"
      ? "text-rose-700"
      : event.outcome === "cancelled"
        ? "text-slate-400"
        : event.outcome === "sent" || event.outcome === "running"
          ? "text-indigo-700"
          : "text-slate-600";
  const detail =
    event.outcome === "failed"
      ? `Failed (${event.detail ?? "error"})`
      : event.detail
        ? decisionLabel(event.detail)
        : event.outcome === "sent"
          ? "Sent, awaiting the answer"
          : event.outcome === "running"
            ? "Running…"
            : event.outcome === "cancelled"
              ? "Not sent"
              : null;
  return (
    <li className="flex items-baseline gap-3 py-1 text-xs">
      <span className="w-16 shrink-0 tabular-nums text-slate-400">{formatDateTime(event.at, "HH:mm:ss")}</span>
      <span className="min-w-0 flex-1 truncate text-slate-700">
        {event.external && (
          <span className="mr-1.5 rounded bg-indigo-50 px-1 py-px text-[10px] font-semibold uppercase tracking-wider text-indigo-700">
            API
          </span>
        )}
        {event.label}
        {event.company && <span className="text-slate-500"> · {event.company}</span>}
      </span>
      {detail && <span className={cn("shrink-0", tone)}>{detail}</span>}
      {event.cost != null && event.cost > 0 && (
        <span className="shrink-0 tabular-nums text-slate-400">{costLabel(event.cost, event.unit)}</span>
      )}
    </li>
  );
}

/** The import step by step (owner, 2026-09-28): rounds, collection, provider API calls,
 *  scoring and cohort ranks, then what every call did and the latest operations. */
export function FundingProgressPanel({ progress }: { progress: CfoFundingProgress }) {
  const eta = progress.complete ? null : etaLabel(progress.eta_seconds);
  const decisions = Object.entries(progress.decisions).sort((a, b) => b[1] - a[1]);
  const decided = decisions.reduce((sum, [, n]) => sum + n, 0);
  const missing = progress.decisions.NEEDS_DATA ?? 0;
  return (
    <div className="space-y-4">
      <section className="space-y-3 rounded-xl border border-slate-200 bg-white p-4">
        <div className="flex items-baseline justify-between gap-3">
          <h3 className="text-sm font-semibold text-slate-900">Progress</h3>
          <span className="text-xs text-slate-500">
            {progress.complete
              ? "Nothing left to run"
              : eta
                ? eta.charAt(0).toUpperCase() + eta.slice(1)
                : progress.last_activity_at
                  ? `Last activity ${formatRelativeDate(progress.last_activity_at)}`
                  : null}
          </span>
        </div>
        {progress.stalled && (
          <div
            role="alert"
            className="flex gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900"
          >
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span>
              Nothing has run for a minute although work is queued: the engine may be stopped.
            </span>
          </div>
        )}
        <ul className="space-y-3">
          {progress.stages.map((stage) => (
            <Stage key={stage.key} stage={stage} />
          ))}
        </ul>
      </section>

      {progress.calls.length > 0 && (
        <section className="space-y-2">
          <div className="flex items-baseline justify-between gap-3">
            <h3 className="text-[11px] font-medium uppercase tracking-wider text-slate-500">
              Provider API calls
            </h3>
            {!progress.external_calls && (
              <span className="text-[11px] text-amber-700">External calls are off</span>
            )}
          </div>
          <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
            <table className="w-full min-w-[560px] text-xs">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-400">
                <tr>
                  <th className="px-3 py-2 text-left font-medium">Provider</th>
                  <th className="px-3 py-2 text-left font-medium">Collects</th>
                  <th className="px-3 py-2 text-right font-medium">Requests</th>
                  <th className="px-3 py-2 text-right font-medium">Not sent</th>
                  <th className="px-3 py-2 text-right font-medium">Sent</th>
                  <th className="px-3 py-2 text-right font-medium">Answered</th>
                  <th className="px-3 py-2 text-right font-medium">Failed</th>
                  <th className="px-3 py-2 text-right font-medium">Cost</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {progress.calls.map((call) => (
                  <tr key={call.operation}>
                    <td className="px-3 py-2 font-medium text-slate-800">{call.provider}</td>
                    <td className="px-3 py-2 text-slate-600">{call.label}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-slate-700">{call.requests ?? 0}</td>
                    <td className={cn("px-3 py-2 text-right tabular-nums", call.waiting ? "text-amber-700" : "text-slate-400")}>
                      {call.waiting ?? 0}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-slate-700">{call.sent ?? 0}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-emerald-700">{call.succeeded ?? 0}</td>
                    <td className={cn("px-3 py-2 text-right tabular-nums", call.failed ? "text-rose-700" : "text-slate-400")}>
                      {call.failed ?? 0}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-slate-600">{costLabel(call.cost, call.unit)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {decided > 0 && (
        <section className="space-y-2">
          <h3 className="text-[11px] font-medium uppercase tracking-wider text-slate-500">
            Decisions · {decided} of {progress.companies} companies
          </h3>
          <div className="flex flex-wrap gap-1.5">
            {decisions.map(([decision, count]) => (
              <span
                key={decision}
                className={cn(
                  "rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset",
                  CFO_DECISIONS[decision]?.className ?? "bg-slate-100 text-slate-700 ring-slate-200",
                )}
              >
                {decisionLabel(decision)} · {count}
              </span>
            ))}
          </div>
          {missing > 0 && !progress.external_calls && (
            <p className="text-xs text-slate-500">
              Missing data: the team, hiring and founder data come from the provider calls,
              which are off.
            </p>
          )}
        </section>
      )}

      {progress.recent.length > 0 && (
        <section className="space-y-1">
          <h3 className="text-[11px] font-medium uppercase tracking-wider text-slate-500">
            Latest operations
          </h3>
          <ul className="divide-y divide-slate-50 rounded-xl border border-slate-200 bg-white px-3 py-1">
            {progress.recent.map((event, i) => (
              <Event key={`${event.at}-${event.operation}-${i}`} event={event} />
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
